import { createRef, useEffect, useMemo, useRef, useState } from "react";

import { useDispatch, useSelector } from "react-redux";
import {
  selectKitchen,
  selectOnlineOrdersConfigs,
} from "../../store/kitchen/kitchen.selector";
import {
  selectCartCount,
  selectCartItems,
  selectCustomerAccountCache,
  selectCartTotal,
  selectCustomerDetails,
  selectDeliveryAddress,
  selectDiscount,
  selectNewCustomer,
  selectOrderNote,
  selectScheduledTime,
} from "../../store/cart/cart.selector";
import {
  analytics,
  createOnlineOrder,
  functions,
} from "../../utils/firebase/firebase.utils";
import { logEvent } from "firebase/analytics";
import {
  CheckoutContainer,
  EmptyCartSubtitleText,
  EmptyCartText,
} from "../checkout/checkout.styles";
import Spinner from "../../components/spinner/spinner.component";
import { httpsCallable } from "firebase/functions";
import {
  ErrorMessageCustomBox,
  ParentContainer,
  PaymentContainer,
  PaymentFormContainer,
  PaymentInfoContainer,
  PaymentInfoText,
  SpinnerOverlay,
  SubmittingText,
  SubmittingSubtext,
} from "./payment-form.styles";
import PaymentButton from "../../components/payment-button/payment-button.component";
import { BUTTON_TYPE_CLASSES } from "../../components/button/button.component";
import { Box } from "@mui/material";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  clearCart,
  setOrderNote,
  setPaymentProcessing,
  setScheduledTime,
} from "../../store/cart/cart.action";
import EmptyCartImage from "../../assets/empty-cart.png";
import {
  googleMerchantId,
  tyroLiveMode,
} from "../../utils/firebase/configKeys";
import {
  getColor,
  getFirstWordAndFirstLetterOfSecondWord,
  getFontName,
} from "../../utils/utils.common";
import { roundedUp } from "../../utils/roundUpNumber";
import { Order } from "../../store/order/order.types";
import PaymentDetails from "./payment-details.component";
import { fieldError } from "../../constants";
import { CartItem } from "../../store/cart/cart.types";
import EmptyState from "../../components/shared/emptyState/emptyState.component";
import ErrorMessage from "../../components/shared/errorMessage/errorMessage.component";
import { useTextColor } from "../../utils/colorContrast/useColorContrast";
import { WhiteAndBlack } from "../../utils/colorContrast/colorContrast";
import tyroLogoBlack from "../../assets/tyro-logo-black.png";
import tyroLogoWhite from "../../assets/tyro-logo-white.png";
import {
  calculateHolidaySurcharge,
  getActiveSurcharge,
} from "../../utils/surcharges";
import { orderTypeToDisplayLabel, resolveOrderType } from "../../utils/orderType";
import { resolveDeliveryFeeAmount } from "../../utils/deliveryConfig";

const tyroJSRef = createRef<any>();
const Payment = () => {
  const onlineOrdersConfig = useSelector(selectOnlineOrdersConfigs);

  const secondaryFontStyle = getFontName(
    "secondary-font-style",
    onlineOrdersConfig
  );
  const backgroundSecondary = getColor(
    "background-secondary",
    onlineOrdersConfig
  );
  const headerModalBackground = getColor(
    "header-modal-background",
    onlineOrdersConfig
  );
  const textBody = getColor("text-body", onlineOrdersConfig);
  const brandPrimary = getColor("brand-primary", onlineOrdersConfig);
  const fieldBorder = getColor("field-border", onlineOrdersConfig);
  const fieldBackground = getColor("field-background", onlineOrdersConfig);
  const fieldFilledText = getColor("field-filled-text", onlineOrdersConfig);

  const defaultSettings = useMemo(
    () => ({
      autoPaySecret: true,
      paySecret: "",
      theme: "default",
      styleProps: {
        fontFamily: secondaryFontStyle,
        bodyPadding: "0px",
        bodyBackgroundColor: headerModalBackground,
        inputBackgroundColor: fieldBackground,
        inputBorderColor: fieldBorder,
        inputBorderSize: "1.5px",
        inputBorderRadius: "8px",
        inputFontSize: "16px",
        inputFontColor: fieldFilledText,
        inputFontWeight: "400",
        inputPadding: "11px 10px",
        inputSpacing: "16px",
        inputErrorFontColor: fieldError,
        inputErrorBorderColor: fieldError,
        inputErrorBorderSize: "1.5px",
        // inputFocusFontColor: "#344054",
        inputFocusBorderColor: brandPrimary,
        inputFocusBorderSize: "1.5px",
        inputFocusBackgroundColor: fieldBackground,
        labelFontColor: textBody,
        labelFontSize: "16px",
        labelFontWeight: "600",
        labelPadding: "0px 0px 6px 0px",
        labelPosition: "block",
        errorFontColor: fieldError,
        errorFontSize: "14px",
        errorFontWeight: "400",
        errorBackgroundColor: headerModalBackground,
        errorPadding: "0px",
        walletPaymentsButtonsHeight: "48px",
        walletPaymentsButtonsMargin: "0px",
        walletPaymentsDividerEnabled: true,
        walletPaymentsButtonsStacking: "VERTICAL",
        showSupportedCards: false,
        showCardIcon: false,
        googlePayButton: {
          buttonType: "pay",
        },
        applePayButton: {
          buttonStyle: "black",
        },
      },
      options: {
        walletButtons: {
          singleButtonOnly: true,
        },
        applePay: {
          enabled: true,
          totalLabel: "Swifti",
        },
        googlePay: {
          enabled: true,
          merchantInfo: {
            merchantName: "Swifti",
            merchantId: googleMerchantId,
          },
        },
      },
    }),
    []
  );

  const [configuration, setConfiguration] = useState(() => defaultSettings);
  const [error, setError] = useState<{
    type: string;
    message: string;
    errorCode: number;
    debug: any;
  }>({ type: null, message: null, errorCode: null, debug: null });
  const [loading, setLoading] = useState(true);
  const [libraryReady, setLibraryReady] = useState(false);
  const [fetchingPaySecret, setFetchingPaySecret] = useState(false);
  const [tyroPayRequestId, setTyroPayRequestId] = useState(null);
  const [orderId, setOrderId] = useState(null);

  const [isFormLoading, setIsFormLoading] = useState(true);

  const [payRequestReady, setPayRequestReady] = useState(false);
  const [payFormReady, setPayFormReady] = useState(false);
  const [submittingOverlay, setSubmittingOverlay] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const cartCount = useSelector(selectCartCount);

  const kitchen = useSelector(selectKitchen);
  const cartItems = useSelector(selectCartItems);
  const cartSubtotal = useSelector(selectCartTotal);
  const discountData = useSelector(selectDiscount);
  const newCustomer = useSelector(selectNewCustomer);
  const customerAccountCache = useSelector(selectCustomerAccountCache);

  // const cardFee = (cartSubtotal * 0.0175).toFixed(2);
  const customerDetails = useSelector(selectCustomerDetails);

  const getScheduledTime = useSelector(selectScheduledTime);
  const orderNote = useSelector(selectOrderNote);
  const deliveryAddress = useSelector(selectDeliveryAddress);

  const paymentLogoColor = useTextColor(headerModalBackground);

  const searchParams = new URLSearchParams(window.location.search);

  const tableNumber = searchParams.get("tableNumber");

  const getOrderType = resolveOrderType(searchParams, onlineOrdersConfig);
  const deliveryFee =
    getOrderType === "delivery"
      ? resolveDeliveryFeeAmount(kitchen, cartSubtotal)
      : 0;

  const cardFeePercent = onlineOrdersConfig?.cardFeePercent
    ? onlineOrdersConfig?.cardFeePercent / 100
    : 0;
  const cardFeeFixedCharge = onlineOrdersConfig?.cardFeeFixedCharge
    ? onlineOrdersConfig?.cardFeeFixedCharge
    : 0;

  const activeSurcharge = getActiveSurcharge(onlineOrdersConfig, kitchen?.timezone);
  const holidaySurcharge = roundedUp(
    calculateHolidaySurcharge(cartSubtotal, activeSurcharge)
  );
  const baseBeforeCardSurcharge = roundedUp(
    Number(cartSubtotal) + Number(holidaySurcharge) + Number(deliveryFee)
  );
  const cardTotalFee = roundedUp(
    baseBeforeCardSurcharge * cardFeePercent + cardFeeFixedCharge
  );
  const cartTotal = roundedUp(
    Number(baseBeforeCardSurcharge) + Number(cardTotalFee)
  );

  let totalQuantity =
    cartItems && cartItems.length > 0
      ? cartItems.reduce((acc, cartItem) => acc + cartItem.quantity, 0)
      : 0;

  const navigate = useNavigate();
  const { kitchenSlug } = useParams();
  const location = useLocation();

  const dispatch = useDispatch();

  const isPaymentProcessing = submitting || submittingOverlay;
  const isInitialPaymentFormLoading =
    (cartItems.length !== 0 && loading) || isFormLoading;

  useEffect(() => {
    dispatch(setPaymentProcessing(isPaymentProcessing));
  }, [dispatch, isPaymentProcessing]);

  useEffect(() => {
    return () => {
      dispatch(setPaymentProcessing(false));
    };
  }, [dispatch]);

  useEffect(() => {
    if (!isPaymentProcessing) {
      return;
    }

    const pushHistoryGuard = () => {
      window.history.pushState({ paymentProcessingGuard: true }, "");
    };

    pushHistoryGuard();

    const handlePopState = () => {
      pushHistoryGuard();
    };

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };

    window.addEventListener("popstate", handlePopState);
    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("popstate", handlePopState);
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [isPaymentProcessing]);

  // log payment page visited
  useEffect(() => {
    logEvent(analytics, "payment_page_visited", {
      kitchenName: kitchen?.kitchenName,
    });
  }, [kitchen]);

  const { paySecret, theme, styleProps, options } = configuration;

  const customerName = customerDetails?.customerName
    ? getFirstWordAndFirstLetterOfSecondWord(customerDetails?.customerName)
    : null;

  const customerEmail = customerDetails?.customerEmail
    ? customerDetails.customerEmail
    : null;
  const customerMobileNumber = customerDetails?.customerMobile
    ? customerDetails.customerMobile
    : null;

  const customerDetailsEmailNormalized = customerEmail?.trim().toLowerCase() || "";
  const customerDetailsMobileNormalized = (customerMobileNumber || "").replace(
    /\D/g,
    ""
  );
  const customerDetailsIdentityKey = `${customerDetailsEmailNormalized}|${customerDetailsMobileNormalized}`;

  const newCustomerEmailNormalized = newCustomer?.email?.trim().toLowerCase() || "";
  const newCustomerMobileNormalized = (newCustomer?.mobileNumber || "").replace(
    /\D/g,
    ""
  );
  const newCustomerIdentityKey = `${newCustomerEmailNormalized}|${newCustomerMobileNormalized}`;

  const activeIdentityKey = newCustomer ? newCustomerIdentityKey : customerDetailsIdentityKey;
  const customerAccountIdFromCache =
    customerAccountCache &&
    customerAccountCache.kitchenId === kitchen?.kitchenId &&
    customerAccountCache.identityKey === activeIdentityKey
      ? customerAccountCache.customerAccountId
      : null;

  const hasCustomerAccountFlow = Boolean(newCustomer || customerAccountIdFromCache);
  const customerAccountEmail = hasCustomerAccountFlow
    ? newCustomer?.email?.trim().toLowerCase() || customerDetailsEmailNormalized || null
    : null;
  const customerAccountMobile = hasCustomerAccountFlow
    ? newCustomer?.mobileNumber?.trim() || customerMobileNumber || null
    : null;
  const customerAccountFirstName = hasCustomerAccountFlow
    ? newCustomer?.firstName || null
    : null;
  const customerAccountLastName = hasCustomerAccountFlow
    ? newCustomer?.lastName || null
    : null;

  const discountName = discountData?.name ? discountData.name : undefined;
  const discountType = discountData?.type ? discountData.type : undefined;
  const discountValue = discountData?.value
    ? Number(discountData.value)
    : undefined;

  const baseTotal =
    cartItems?.reduce((sum, item) => sum + item.quantity * item.dishPrice, 0) ??
    0;

  const totalWithVariants =
    cartItems?.reduce(
      (sum, item) => sum + item.quantity * item.dishPriceWithVariants,
      0
    ) ?? 0;

  const discountFromBaseTotal =
    discountType === "percentage"
      ? Number(((baseTotal * discountValue) / 100).toFixed(2))
      : discountType === "amount"
      ? Number(Number(discountValue).toFixed(2))
      : 0;

  const discountFromVariantsTotal =
    discountType === "percentage"
      ? Number(((totalWithVariants * discountValue) / 100).toFixed(2))
      : discountType === "amount"
      ? Number(Number(discountValue).toFixed(2))
      : 0;

  let orderType = orderTypeToDisplayLabel(getOrderType);

  const discountModel = discountName
    ? ([
        {
          category: "Discounts",
          categoryId: "custom-discounts",
          discountReason: discountName,
          discountType,
          discountValue,
          dishName: "Discount",
          // Keep discount line pricing consistent across fields for DB consumers.
          dishPrice: -discountFromVariantsTotal,
          dishPriceWithVariants: -discountFromVariantsTotal,
          quantity: 1,
        },
      ] as CartItem[])
    : [];

  // Order object to write
  const order: Order = {
    abn: kitchen?.abn,
    cartItems: [...cartItems, ...discountModel],
    cartSubtotal,
    createdAt: new Date(),
    ...(hasCustomerAccountFlow
      ? {
          customerDetails: null,
          customerAccountId: customerAccountIdFromCache,
          customerAccountCompanyName: null,
          customerAccountFirstName: customerAccountFirstName,
          customerAccountLastName: customerAccountLastName,
          customerAccountIsTabEnabled: false,
          customerAccountEmail: customerAccountEmail,
          customerAccountMobileNumber: customerAccountMobile,
          customerAccountCreationSource: newCustomer?.creationSource || "online",
          customerAccountMarketingOffersConsent:
            typeof newCustomer?.marketingOffersConsent === "boolean"
              ? newCustomer.marketingOffersConsent
              : null,
        }
      : {
          customerDetails: {
            customerMobileNumber,
            customerName: customerName,
            customerEmail,
          },
        }),
    kitchenAddress: kitchen?.fullAddress,
    kitchenEmail: kitchen?.email,
    kitchenId: kitchen?.kitchenId,
    kitchenName: kitchen?.kitchenName,
    kitchenPhoneNumber: kitchen?.phoneNumber,
    kitchenPushToken: kitchen?.kitchenPushToken
      ? kitchen.kitchenPushToken
      : null,
    orderMethod: "online",
    orderPayedAt: null,
    orderStatus: "New_Order_Processing",
    orderType: orderType,
    orderWaitTime:
      getOrderType === "delivery"
        ? kitchen?.deliveryConfig?.deliveryMinimumTime || 15
        : onlineOrdersConfig?.orderReadyTime || 15,
    paymentStatus: "unpaid",
    paymentType: "card",
    postPay: false,
    scheduledPickupTime:
      getOrderType === "takeaway" ? getScheduledTime : null,
    scheduledDeliveryTime:
      getOrderType === "delivery" ? getScheduledTime : null,
    tableNumber: getOrderType === "delivery" ? null : tableNumber,
    deliveryAddress: getOrderType === "delivery" ? deliveryAddress : null,
    deliveryFee: getOrderType === "delivery" ? deliveryFee : 0,
    totalAmount: cartTotal, // with all fees (hoilday surcharge, etc..)
    totalQuantity,
    tyroLocationID: onlineOrdersConfig?.tyroLocationId,
    tyroPayRequestId,
    orderId,
    orderNote,
    cardSurcharge: cardTotalFee,
    holidaySurcharge,
    surcharge: activeSurcharge,
  };

  let tyro = useRef();
  const hasRetriedInitRef = useRef(false);
  const isInitializingRef = useRef(false);
  const lastFetchedCartTotalRef = useRef(null);
  const timingRefs = useRef({
    librarySecretTimerStarted: false,
    initReadyTimerStarted: false,
  });
  // STEP 1, Attach TyroJS Library
  // Installs the TyroJS Library onto the DOM
  useEffect(() => {
    // console.log("STEP 1 STARTED");

    // If Tyro.js is already present (loaded via index.html head), mark library as ready.
    // Otherwise, inject it once.
    // @ts-ignore
    if (window.Tyro || document.getElementById("tyro-js-library")) {
      setLibraryReady(true);
      return;
    }

    const script = document.createElement("script");
    script.id = "tyro-js-library";
    script.src = "https://pay.connect.tyro.com/v1/tyro.js";
    script.crossOrigin = "anonymous";
    script.async = true;
    script.onload = () => setLibraryReady(true);
    document.body.appendChild(script);

    // console.log("STEP 1 DONE");
  }, []);

  // If navigation passed a pre-fetched paySecret, use it immediately
  useEffect(() => {
    // @ts-ignore
    const navTyro = location?.state?.tyro;
    if (navTyro?.paySecret) {
      const storedCartTotal = navTyro?.cartTotal;
      // Validate cartTotal matches - if not, clear paySecret (it's stale)
      if (storedCartTotal !== undefined && storedCartTotal !== cartTotal) {
        setConfiguration((prev) => ({ ...prev, paySecret: "" }));
        lastFetchedCartTotalRef.current = null;
        // Reset timer flags when clearing paySecret to prevent timer conflicts
        if (process.env.NODE_ENV === "development") {
          if (timingRefs.current.librarySecretTimerStarted) {
            try {
              console.timeEnd("tyro:library+secret:ready");
            } catch (e) {
              // Timer might have already been ended, ignore
            }
            timingRefs.current.librarySecretTimerStarted = false;
            timingRefs.current.initReadyTimerStarted = false;
          }
        }
      } else {
        setOrderId(navTyro?.orderId || null);
        setTyroPayRequestId(navTyro?.payRequestId || null);
        setConfiguration((prev) => ({ ...prev, paySecret: navTyro.paySecret }));
        lastFetchedCartTotalRef.current = cartTotal;
      }
    }
  }, [location?.state, cartTotal]);

  // STEP 2, Fetch the Pay Secret (mount-time with real deps)
  useEffect(() => {
    setError(null);
    // Check navigation state first - if it has paySecret, validate it's not stale
    const navTyro = location?.state?.tyro;
    if (navTyro?.paySecret) {
      const storedCartTotal = navTyro?.cartTotal;
      // If navigation state paySecret is stale (cartTotal mismatch), we need to fetch
      if (storedCartTotal !== undefined && storedCartTotal !== cartTotal) {
        // Navigation state has stale paySecret, fall through to fetch
      } else {
        // Navigation state has valid paySecret, don't fetch
        return;
      }
    }
    
    if (
      !paySecret?.length &&
      !fetchingPaySecret &&
      cartItems.length > 0 &&
      customerDetails !== undefined &&
      customerDetails?.customerName !== null
    ) {
      fetchPaySecret();
    } else if (paySecret?.length) {
      setLoading(false);
    }
  }, [customerDetails, cartItems.length, fetchingPaySecret, paySecret, location?.state, cartTotal]);

  // Fetch the Pay Secret
  // This would be replaced with your own implementation to obtain your pay request
  const fetchPaySecret = async () => {
    setFetchingPaySecret(true);
    try {
      const callFunction = httpsCallable(
        functions,
        "tyroEndpoints-createOrder"
      );

      console.log(cartTotal);

      const response: any = await callFunction({
        cartTotal: cartTotal,
        tyroLocationID: onlineOrdersConfig?.tyroLocationId,
      });

      const responseStatus = response.data.statusCode;

      if (responseStatus !== 200) {
        setLoading(false);
        setError({
          type: "critical",
          errorCode: responseStatus,
          message:
            "Error occurred while loading checkout page. Please try again later",
          debug: { response },
        });
      }
      // console.log(responseStatus);
      const paySecret = response.data.paySecret;
      const payRequestId = response.data?.payRequestId
        ? response.data.payRequestId
        : null;
      const orderId = response.data?.orderId ? response.data.orderId : null;

      setOrderId(orderId);
      setTyroPayRequestId(payRequestId);
      setConfiguration((prev) => ({ ...prev, paySecret }));
      lastFetchedCartTotalRef.current = cartTotal;
    } catch (error: any) {
      setError({
        type: "critical",
        errorCode: error.errorCode,
        message: "Failed to generate Payment form. Please try again later.",
        debug: { error },
      });
      setLoading(false);
      setIsFormLoading(false);
    }
    setFetchingPaySecret(false);
  };

  // STEP 3, Load the Pay Request into TyroJS
  useEffect(() => {
    if (process.env.NODE_ENV === "development") {
      // Start timer when we first have either library or secret ready
      if ((libraryReady || paySecret?.length) && !timingRefs.current.librarySecretTimerStarted) {
        console.time("tyro:library+secret:ready");
        timingRefs.current.librarySecretTimerStarted = true;
      }
    }
    
    // Guard: prevent multiple simultaneous initializations
    if (libraryReady && paySecret?.length && !isInitializingRef.current) {
      if (process.env.NODE_ENV === "development") {
        // End library+secret timer and start init timer (only once)
        if (timingRefs.current.librarySecretTimerStarted && !timingRefs.current.initReadyTimerStarted) {
          console.timeEnd("tyro:library+secret:ready");
          console.time("tyro:init→ready");
          timingRefs.current.initReadyTimerStarted = true;
        }
      }
      initPayRequest();
      // console.log("STEP 3 DONE");
    }

    // Cleanup: if paySecret is cleared, reset timer flags
    return () => {
      if (!paySecret?.length && timingRefs.current.librarySecretTimerStarted) {
        if (process.env.NODE_ENV === "development") {
          // End timer if it was started but not ended
          try {
            console.timeEnd("tyro:library+secret:ready");
          } catch (e) {
            // Timer might have already been ended, ignore
          }
        }
        timingRefs.current.librarySecretTimerStarted = false;
        timingRefs.current.initReadyTimerStarted = false;
      }
    };
  }, [libraryReady, paySecret]);

  // Initialize Tyro.js with the Pay Request
  const initPayRequest = async () => {
    // Guard: prevent multiple simultaneous initializations
    if (isInitializingRef.current) {
      return;
    }
    // Set flag immediately to prevent multiple calls
    isInitializingRef.current = true;
    
    setLoading(true);
    setIsFormLoading(true);
    setPayRequestReady(false);
    setPayFormReady(false);
    setSubmitting(false);
    let orderId = null;

    try {
      if (process.env.NODE_ENV === "development") {
        console.time("tyro:init");
      }
      // @ts-ignore
      if (window.__tyroPreWarmedInstance) {
        // @ts-ignore
        tyro.current = window.__tyroPreWarmedInstance;
        // @ts-ignore
        delete window.__tyroPreWarmedInstance;
      } else {
        // @ts-ignore
        tyro.current = new window.Tyro({
          liveMode: tyroLiveMode,
        });
      }

      // @ts-ignore
      await tyro.current.init(paySecret);
      if (process.env.NODE_ENV === "development") {
        console.timeEnd("tyro:init");
      }

      const payFormElement = document.getElementById("tyro-pay-form");
      if (payFormElement !== null) {
        payFormElement.innerHTML = "";
      }
      // @ts-ignore
      const payForm = tyro.current.createPayForm({
        theme,
        styleProps,
        options,
      });

      payForm.setWalletPaymentBeginListener(async (paymentType) => {
        orderId = await createOnlineOrder(order);

        setError(null);
        setSubmittingOverlay(true);
        setSubmitting(true);

        if (paymentType === "APPLE_PAY") {
          // optionally do something specific to Apple Pay
        } else if (paymentType === "GOOGLE_PAY") {
          // optionally do something specific to Google Pay
        }
      });
      payForm.setWalletPaymentCancelledListener((paymentType) => {
        setSubmittingOverlay(false);
        setSubmitting(false);

        if (paymentType === "APPLE_PAY") {
          // optionally do something specific to Apple Pay
        } else if (paymentType === "GOOGLE_PAY") {
          // optionally do something specific to Google Pay
        }
      });

      payForm.setWalletPaymentCompleteListener((paymentType, error) => {
        if (error) {
          setError({
            type: "critical",
            errorCode: error.errorCode,
            message: `${error.toString()}`,
            debug: { error },
          });
          setSubmittingOverlay(false);
          setSubmitting(false);
        } else {
          getPaymentResult(orderId);
        }
      });

      // Javascript code sample
      payForm.setReadyListener(() => {
        setIsFormLoading(false);
        setLoading(false);
        if (process.env.NODE_ENV === "development") {
          console.timeEnd("tyro:init→ready");
        }
      });

      payForm.inject("#tyro-pay-form");
      setPayRequestReady(true);
      setPayFormReady(true);
      isInitializingRef.current = false;
      // console.log("STEP 4 READY");
    } catch (error: any) {
      // End timer if it was started
      if (process.env.NODE_ENV === "development") {
        console.timeEnd("tyro:init");
      }
      // One-shot retry if paySecret might be invalid/expired
      if (!hasRetriedInitRef.current) {
        hasRetriedInitRef.current = true;
        isInitializingRef.current = false; // Reset so retry can initialize
        try {
          await fetchPaySecret();
          // After refetch, init will be triggered by the effect watching paySecret
          return;
        } catch (refetchErr) {
          // fall through to error display
        }
      }
      setError({
        type: "critical",
        errorCode: error?.errorCode,
        message: `${error?.toString()}`,
        debug: { error },
      });
      setLoading(false);
      setIsFormLoading(false);
      isInitializingRef.current = false;
    }
  };

  // STEP 5, Handle submitting the payment
  async function submitPayForm(event) {
    event.preventDefault(); // Prevent the default form submission behavior

    // if (name !== null && name !== "" && email !== null && email !== "") {
    setError(null);
    setSubmitting(true);
    setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 500);
    let result;
    try {
      // write order
      const orderId = await createOnlineOrder(order);
      // @ts-ignore
      result = await tyro.current.submitPay();

      await getPaymentResult(orderId);
    } catch (error: any) {
      if (error?.type === "CLIENT_VALIDATION_ERROR" && !error?.errorCode) {
        // can ignore these errors as handled by validation
      } else {
        window.scrollTo({ top: 1000, behavior: "smooth" });

        // display other errors
        setError({
          type: "critical",
          errorCode: error.errorCode,
          message: `${error.toString()}`,
          debug: { error, result },
        });
      }
      setSubmitting(false);
    }
  }

  async function getPaymentResult(orderId: string) {
    // @ts-ignore
    const payRequest = await tyro.current.fetchPayRequest();

    if (payRequest.status === "SUCCESS") {
      // Confirmation (finalize, customer link, email) runs in tyroPaymentWebhook.
      // Success page polls order confirmation until paymentStatus/orderCount appear.
      navigate(`/${kitchenSlug}/checkout/success`, {
        state: { order: { ...order, orderId } },
      });
      setSubmitting(false);
      dispatch(setOrderNote(null));
      dispatch(setScheduledTime(null));
      dispatch(clearCart());
    } else {
      setError({
        type: "invalid",
        errorCode: payRequest.errorCode,
        message: `${payRequest.status}`,
        debug: { payRequest },
      });
      setSubmitting(false);
    }
    setSubmittingOverlay(false);
  }

  if (cartItems.length === 0) {
    return (
      <CheckoutContainer>
        <EmptyState
          largeText="Your cart is empty."
          normalText="Go back to the menu and add some items."
          activeBgHeaderModal
          backButton
        />
      </CheckoutContainer>
    );
  }

  if (
    customerDetails === undefined ||
    customerDetails?.customerName === null ||
    customerDetails?.customerEmail === null
  ) {
    return (
      <Box
        sx={{
          backgroundColor: headerModalBackground,
          minHeight: "calc(100vh - 72px)",
          "@media screen and (max-width: 768px)": {
            minHeight: "calc(100vh - 64px)",
          },
        }}
      >
        <CheckoutContainer
          style={{
            textAlign: "center",
            justifyContent: "center",
            display: "flex",
            alignContent: "center",
            alignItems: "center",
            paddingTop: "72px",
            paddingBottom: "72px",
          }}
        >
          {/* <img src={EmptyCartImage} style={{ width: '150px', paddingBottom: '24px' }} alt='Empty Cart' /> */}
          <EmptyCartText>Some of your details are missing</EmptyCartText>
          <EmptyCartSubtitleText>
            Go back to the checkout page to add them
          </EmptyCartSubtitleText>
        </CheckoutContainer>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        backgroundColor: headerModalBackground,
        minHeight: "calc(100vh - 72px)",
        "@media screen and (max-width: 768px)": {
          minHeight: "calc(100vh - 64px)",
        },
      }}
    >
      <PaymentContainer ref={tyroJSRef}>
        <PaymentFormContainer onSubmit={submitPayForm}>
          {isPaymentProcessing ? null : (
            <PaymentDetails
              kitchenSlug={kitchenSlug}
              getOrderType={getOrderType}
              getTableNumber={tableNumber}
              deliveryAddress={deliveryAddress}
              cartTotal={cartTotal}
              cartCount={cartCount}
              getScheduledTime={getScheduledTime}
              onlineOrdersConfig={onlineOrdersConfig}
              kitchen={kitchen}
              customerDetails={customerDetails}
            />
          )}
          {(isInitialPaymentFormLoading || isPaymentProcessing) && (
            <SpinnerOverlay backgroundSecondary={backgroundSecondary}>
              <Spinner size={64} color={brandPrimary} />
              {isPaymentProcessing && (
                <>
                  <SubmittingText>Processing Payment</SubmittingText>
                  <SubmittingSubtext>Don&apos;t close the page</SubmittingSubtext>
                </>
              )}
            </SpinnerOverlay>
          )}
          <ParentContainer
            overlayState={isInitialPaymentFormLoading || isPaymentProcessing}
          >
            <div id="pay-form">
              <div id="tyro-pay-form"></div>
            </div>
            {error?.type && (
              <ErrorMessageCustomBox>
                <ErrorMessage errorMessage={error.message} radiusStyle />
              </ErrorMessageCustomBox>
            )}
            <PaymentButton
              id="pay-form-submit"
              buttonType={BUTTON_TYPE_CLASSES.cart}
              text="Pay now"
              disabled={isPaymentProcessing}
              style={{ maxWidth: "100%" }}
              isLoading={undefined}
              cartItems={undefined}
              onClick={undefined}
            />
            <PaymentInfoContainer>
              <PaymentInfoText textBody={textBody}>
                Payments Powered by
              </PaymentInfoText>
              {
                <img
                  width={49}
                  src={
                    paymentLogoColor === WhiteAndBlack.black
                      ? tyroLogoBlack
                      : tyroLogoWhite
                  }
                  alt="Pay securely with Tyro at Pretzel"
                />
              }
            </PaymentInfoContainer>
          </ParentContainer>
        </PaymentFormContainer>
      </PaymentContainer>
    </Box>
  );
};

export default Payment;
